import { z } from 'zod';
import { PROVIDER_IDS } from '../providers.js';

export const baseIntegrationConfigSchema = z.object({
  enabled: z.boolean().default(true),
  webhooksEnabled: z.boolean().optional(),
});

export const attioConfigSchema = baseIntegrationConfigSchema.extend({
  apiKey: z.string().min(1, 'API key is required'),
  webhookSecret: z.string().optional(),
  sourceFieldSlug: z.string().optional(),
  syncCompanies: z.boolean().default(true),
  syncContacts: z.boolean().default(true),
  syncDeals: z.boolean().default(true),
});

export const odooConfigSchema = baseIntegrationConfigSchema.extend({
  url: z.string().url('Invalid Odoo URL'),
  database: z.string().min(1, 'Database name is required'),
  username: z.string().min(1, 'Username is required'),
  apiKey: z.string().min(1, 'API key is required'),
  syncInvoices: z.boolean().default(true),
  syncSubscriptions: z.boolean().default(true),
});

export const notionConfigSchema = baseIntegrationConfigSchema.extend({
  apiKey: z.string().min(1, 'API key is required'),
  databaseIds: z.object({
    companies: z.string().optional(),
    contacts: z.string().optional(),
    deals: z.string().optional(),
  }),
});

export const sequestoAppConfigSchema = baseIntegrationConfigSchema.extend({
  apiKey: z.string().min(1, 'API key is required'),
  apiUrl: z.string().url('Invalid API URL').optional(),
});

export const resendConfigSchema = baseIntegrationConfigSchema.extend({
  apiKey: z.string().min(1, 'API key is required'),
  fromEmail: z.string().email('Invalid from email'),
  fromName: z.string().min(1, 'From name is required'),
});

export const slackBotSchema = z.object({
  token: z.string().optional(),
  appName: z.string().min(1),
});

export const slackConfigSchema = baseIntegrationConfigSchema.extend({
  bots: z.array(slackBotSchema).optional(),
  botToken: z.string().optional(),
  userToken: z.string().optional(),
  defaultChannel: z.string().optional(),
});

export const slackTodoConfigSchema = baseIntegrationConfigSchema.extend({
  botToken: z.string().min(1, 'Bot token is required'),
  todoChannel: z.string().optional(),
});

export const googleAnalyticsConfigSchema = baseIntegrationConfigSchema.extend({
  propertyId: z.string().min(1, 'Property ID is required'),
  serviceAccountKey: z.string().min(1, 'Service account key is required'),
});

export const customerioConfigSchema = baseIntegrationConfigSchema.extend({
  region: z.enum(['us', 'eu']).default('us'),
  appApiKey: z.string().min(1, 'App API Key is required'),
  siteId: z.string().min(1, 'Site ID is required'),
  trackApiKey: z.string().min(1, 'Track API Key is required'),
  webhookSigningSecret: z.string().optional(),
});

export const lemlistConfigSchema = baseIntegrationConfigSchema.extend({
  apiKey: z.string().min(1, 'API key is required'),
  /** How far back the first sync reaches. Later syncs are incremental. */
  backfillDays: z.coerce.number().int().positive().max(365).optional(),
});

export const googleAdsConfigSchema = baseIntegrationConfigSchema.extend({
  developerToken: z.string().min(1, 'Developer token is required'),
  customerId: z.string().min(1, 'Customer ID is required'),
  loginCustomerId: z.string().optional(),
  refreshToken: z.string().optional(),
  clientId: z.string().optional(),
  clientSecret: z.string().optional(),
});

export const metaAdsConfigSchema = baseIntegrationConfigSchema.extend({
  accessToken: z.string().min(1, 'Access token is required'),
  adAccountId: z.string().min(1, 'Ad account ID is required'),
  pixelId: z.string().optional(),
  customAudienceId: z.string().optional(),
});

export const bingAdsConfigSchema = baseIntegrationConfigSchema.extend({
  developerToken: z.string().min(1, 'Developer token is required'),
  accountId: z.string().min(1, 'Account ID is required'),
  customerId: z.string().min(1, 'Customer ID is required'),
  clientId: z.string().optional(),
  clientSecret: z.string().optional(),
  refreshToken: z.string().optional(),
  accessToken: z.string().optional(),
});

export const linkedinAdsConfigSchema = baseIntegrationConfigSchema.extend({
  accessToken: z.string().min(1, 'Access token is required'),
  refreshToken: z.string().optional(),
  adAccountId: z.string().min(1, 'Ad account ID is required'),
});

export const filloutConfigSchema = baseIntegrationConfigSchema.extend({
  apiKey: z.string().min(1, 'API key is required'),
});

export const anthropicConfigSchema = baseIntegrationConfigSchema.extend({
  apiKey: z.string().min(1, 'API key is required'),
  defaultModel: z.string().optional(),
});

export const auth0ConfigSchema = baseIntegrationConfigSchema.extend({
  domain: z.string().min(1, 'Auth0 domain is required'),
  clientId: z.string().min(1, 'Client ID is required'),
  clientSecret: z.string().min(1, 'Client Secret is required'),
  syncLogs: z.boolean().default(true),
  logRetentionDays: z.number().min(1).max(90).default(30),
});

export const CALENDLY_OWNER_SLUGS = ['patrick', 'andrew', 'nadia'] as const;
export type CalendlyOwnerSlug = (typeof CALENDLY_OWNER_SLUGS)[number];

export const calendlyConfigSchema = baseIntegrationConfigSchema.extend({
  ownerSlug: z.enum(CALENDLY_OWNER_SLUGS, {
    errorMap: () => ({ message: `Owner must be one of: ${CALENDLY_OWNER_SLUGS.join(', ')}` }),
  }),
  apiKey: z.string().min(1, 'API key is required'),
  organizationUri: z.string().min(1, 'Organization URI is required'),
  webhookSigningKey: z.string().optional(),
});

export const contaboConfigSchema = baseIntegrationConfigSchema.extend({
  apiKey: z.string().min(1, 'API key is required'),
  projectId: z.string().optional(),
  runtimeLabel: z.string().optional(),
});

export const deeplConfigSchema = baseIntegrationConfigSchema.extend({
  apiKey: z.string().min(1, 'API key is required'),
});

export const rudderstackConfigSchema = baseIntegrationConfigSchema.extend({
  accessToken: z.string().min(1, 'Personal Access Token is required'),
  webhookSecret: z.string().optional(),
});

export const uptimeRobotConfigSchema = baseIntegrationConfigSchema.extend({
  apiKey: z.string().min(1, 'API key is required'),
  alertChannelId: z.string().min(1, 'Slack channel ID is required'),
  dmSlackUserIds: z.string().optional(),
});

export const pagespeedConfigSchema = baseIntegrationConfigSchema.extend({
  apiKey: z.string().min(1, 'API key is required'),
  /** Base URL of the site to audit; its /sitemap.xml is crawled for pages. */
  siteUrl: z.string().url('Invalid site URL').optional(),
  /** Override sitemap location (defaults to {siteUrl}/sitemap.xml). */
  sitemapUrl: z.string().url('Invalid sitemap URL').optional(),
  /** Comma-separated form factors (mobile, desktop). Default mobile. */
  formFactors: z.string().optional(),
  /** Cap on pages audited per run (string from the UI). Default 100. */
  maxUrls: z.string().optional(),
  /** Slack channel ID for the daily Lighthouse digest. */
  alertChannelId: z.string().optional(),
  /** Comma/newline-separated Slack user IDs to DM on a new regression. */
  dmSlackUserIds: z.string().optional(),
  /** Point drop vs previous capture that triggers a regression DM. Default 10. */
  regressionThresholdPoints: z.string().optional(),
  /** Category score floor that triggers a regression DM. Default 50. */
  minScoreFloor: z.string().optional(),
});

export const integrationProviderSchema = z.enum(PROVIDER_IDS);

export type AttioConfigInput = z.infer<typeof attioConfigSchema>;
export type OdooConfigInput = z.infer<typeof odooConfigSchema>;
export type NotionConfigInput = z.infer<typeof notionConfigSchema>;
export type SequestoAppConfigInput = z.infer<typeof sequestoAppConfigSchema>;
export type ResendConfigInput = z.infer<typeof resendConfigSchema>;
export type SlackConfigInput = z.infer<typeof slackConfigSchema>;
export type SlackTodoConfigInput = z.infer<typeof slackTodoConfigSchema>;
export type GoogleAnalyticsConfigInput = z.infer<typeof googleAnalyticsConfigSchema>;
export type CustomerioConfigInput = z.infer<typeof customerioConfigSchema>;
export type LemlistConfigInput = z.infer<typeof lemlistConfigSchema>;
export type GoogleAdsConfigInput = z.infer<typeof googleAdsConfigSchema>;
export type MetaAdsConfigInput = z.infer<typeof metaAdsConfigSchema>;
export type BingAdsConfigInput = z.infer<typeof bingAdsConfigSchema>;
export type LinkedInAdsConfigInput = z.infer<typeof linkedinAdsConfigSchema>;
export type FilloutConfigInput = z.infer<typeof filloutConfigSchema>;
export type AnthropicConfigInput = z.infer<typeof anthropicConfigSchema>;
export type Auth0ConfigInput = z.infer<typeof auth0ConfigSchema>;
export type CalendlyConfigInput = z.infer<typeof calendlyConfigSchema>;
export type ContaboConfigInput = z.infer<typeof contaboConfigSchema>;
export type DeepLConfigInput = z.infer<typeof deeplConfigSchema>;
export type UptimeRobotConfigInput = z.infer<typeof uptimeRobotConfigSchema>;
export type PageSpeedConfigInput = z.infer<typeof pagespeedConfigSchema>;

export const googleSearchConsoleConfigSchema = baseIntegrationConfigSchema.extend({
  siteUrl: z.string().min(1, 'Site URL is required'),
  serviceAccountKey: z.string().min(1, 'Service account key is required'),
});

export type GoogleSearchConsoleConfigInput = z.infer<typeof googleSearchConsoleConfigSchema>;
export type RudderstackConfigInput = z.infer<typeof rudderstackConfigSchema>;
